import { useState, useEffect, useCallback } from 'react';
import { supabase } from '@/lib/supabase';
import {
  ACTION_META, ENTITY_LABELS,
  type ActivityEntry, type ActionType, type EntityType,
} from '@/hooks/useActivityLog';

const PAGE_SIZE = 30;

const ENTITY_FILTER_OPTIONS: { value: string; label: string; icon: string }[] = [
  { value: 'all',      label: 'الكل',       icon: 'ri-list-check' },
  { value: 'product',  label: 'المنتجات',   icon: 'ri-shopping-bag-3-line' },
  { value: 'order',    label: 'الطلبات',    icon: 'ri-file-list-3-line' },
  { value: 'discount', label: 'الخصومات',   icon: 'ri-coupon-3-line' },
  { value: 'deal',     label: 'العروض',     icon: 'ri-flashlight-line' },
  { value: 'admin',    label: 'المشرفون',   icon: 'ri-shield-user-line' },
  { value: 'review',   label: 'التقييمات',  icon: 'ri-star-line' },
  { value: 'settings', label: 'الإعدادات',  icon: 'ri-settings-3-line' },
  { value: 'city',     label: 'المدن',      icon: 'ri-map-pin-line' },
];

function formatRelativeTime(dateStr: string): string {
  const diff = Date.now() - new Date(dateStr).getTime();
  const mins = Math.floor(diff / 60000);
  const hours = Math.floor(diff / 3600000);
  const days = Math.floor(diff / 86400000);
  if (mins < 1) return 'الآن';
  if (mins < 60) return `منذ ${mins} دقيقة`;
  if (hours < 24) return `منذ ${hours} ساعة`;
  if (days < 7) return `منذ ${days} يوم`;
  return new Date(dateStr).toLocaleDateString('ar-SA', { year: 'numeric', month: 'short', day: 'numeric' });
}

function formatFullDate(dateStr: string): string {
  return new Date(dateStr).toLocaleString('ar-SA', {
    year: 'numeric', month: 'short', day: 'numeric',
    hour: '2-digit', minute: '2-digit',
  });
}

function getInitials(email: string, name?: string | null): string {
  if (name) return name.charAt(0).toUpperCase();
  return email.charAt(0).toUpperCase();
}

function buildDetailText(entry: ActivityEntry): string {
  const d = entry.details ?? {};
  switch (entry.action_type) {
    case 'product_price_change':
      return `من ${d.old_price ?? '—'} إلى ${d.new_price ?? '—'} ر.س`;
    case 'product_stock_change':
      return `من ${d.old_stock ?? '—'} إلى ${d.new_stock ?? '—'} قطعة`;
    case 'product_name_change':
      return `من "${d.old_name ?? '—'}" إلى "${d.new_name ?? '—'}"`;
    case 'product_category_change':
      return `من "${d.old_category ?? '—'}" إلى "${d.new_category ?? '—'}"`;
    case 'order_status_change':
      return `من "${d.old_status ?? '—'}" إلى "${d.new_status ?? '—'}"`;
    case 'order_cancel':
      return d.reason ? `السبب: ${d.reason}` : '';
    case 'admin_add':
      return d.permissions ? `الصلاحية: ${d.permissions === 'full' ? 'مشرف كامل' : d.permissions === 'orders' ? 'مشرف طلبات' : 'مشرف منتجات'}` : '';
    case 'admin_update':
      return d.permissions ? `الصلاحية: ${d.permissions === 'full' ? 'مشرف كامل' : d.permissions === 'orders' ? 'مشرف طلبات' : 'مشرف منتجات'}` : '';
    default:
      return '';
  }
}

const avatarColors = [
  'bg-slate-200 text-slate-700',
  'bg-emerald-100 text-emerald-700',
  'bg-amber-100 text-amber-700',
  'bg-rose-100 text-rose-700',
  'bg-violet-100 text-violet-700',
  'bg-sky-100 text-sky-700',
];

function getAvatarColor(email: string): string {
  let hash = 0;
  for (let i = 0; i < email.length; i++) hash = email.charCodeAt(i) + ((hash << 5) - hash);
  return avatarColors[Math.abs(hash) % avatarColors.length];
}

export default function ActivityLogViewer() {
  const [entries, setEntries] = useState<ActivityEntry[]>([]);
  const [loading, setLoading] = useState(true);
  const [loadingMore, setLoadingMore] = useState(false);
  const [hasMore, setHasMore] = useState(true);
  const [entityFilter, setEntityFilter] = useState<string>('all');
  const [search, setSearch] = useState('');
  const [dateFrom, setDateFrom] = useState('');
  const [dateTo, setDateTo] = useState('');
  const [offset, setOffset] = useState(0);

  // Stats
  const [stats, setStats] = useState<{
    total: number;
    today: number;
    byEntity: Record<string, number>;
  }>({ total: 0, today: 0, byEntity: {} });

  const fetchStats = useCallback(async () => {
    const { data } = await supabase
      .from('activity_log')
      .select('entity_type, created_at');
    if (!data) return;
    const today = new Date(); today.setHours(0, 0, 0, 0);
    const todayCount = data.filter(r => new Date(r.created_at) >= today).length;
    const byEntity: Record<string, number> = {};
    data.forEach(r => { byEntity[r.entity_type] = (byEntity[r.entity_type] ?? 0) + 1; });
    setStats({ total: data.length, today: todayCount, byEntity });
  }, []);

  const fetchEntries = useCallback(async (reset = false) => {
    if (reset) {
      setLoading(true);
      setOffset(0);
    } else {
      setLoadingMore(true);
    }

    const currentOffset = reset ? 0 : offset;

    let query = supabase
      .from('activity_log')
      .select('*')
      .order('created_at', { ascending: false })
      .range(currentOffset, currentOffset + PAGE_SIZE - 1);

    if (entityFilter !== 'all') query = query.eq('entity_type', entityFilter);
    if (dateFrom) query = query.gte('created_at', dateFrom);
    if (dateTo) query = query.lte('created_at', dateTo + 'T23:59:59');

    const { data } = await query;
    const rows = (data ?? []) as ActivityEntry[];

    if (reset) {
      setEntries(rows);
    } else {
      setEntries(prev => [...prev, ...rows]);
    }
    setHasMore(rows.length === PAGE_SIZE);
    setOffset(currentOffset + rows.length);

    if (reset) setLoading(false);
    else setLoadingMore(false);
  }, [entityFilter, dateFrom, dateTo, offset]);

  useEffect(() => {
    fetchStats();
    fetchEntries(true);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [entityFilter, dateFrom, dateTo]);

  // Realtime subscription
  useEffect(() => {
    const channel = supabase
      .channel('activity-log-realtime')
      .on('postgres_changes', { event: 'INSERT', schema: 'public', table: 'activity_log' }, (payload) => {
        const newEntry = payload.new as ActivityEntry;
        setEntries(prev => [newEntry, ...prev]);
        setStats(s => ({
          ...s,
          total: s.total + 1,
          today: s.today + 1,
          byEntity: { ...s.byEntity, [newEntry.entity_type]: (s.byEntity[newEntry.entity_type] ?? 0) + 1 },
        }));
      })
      .subscribe();
    return () => { supabase.removeChannel(channel); };
  }, []);

  const filtered = entries.filter(e => {
    if (!search) return true;
    const q = search.toLowerCase();
    return (
      (e.entity_name ?? '').toLowerCase().includes(q) ||
      e.admin_email.toLowerCase().includes(q) ||
      (e.admin_name ?? '').toLowerCase().includes(q) ||
      (e.entity_id ?? '').toLowerCase().includes(q)
    );
  });

  // Group by date
  const grouped: { date: string; items: ActivityEntry[] }[] = [];
  filtered.forEach(entry => {
    const dateKey = new Date(entry.created_at).toLocaleDateString('ar-SA', {
      weekday: 'long', year: 'numeric', month: 'long', day: 'numeric',
    });
    const last = grouped[grouped.length - 1];
    if (last && last.date === dateKey) {
      last.items.push(entry);
    } else {
      grouped.push({ date: dateKey, items: [entry] });
    }
  });

  return (
    <div className="flex flex-col gap-5">

      {/* Stats Row */}
      <div className="grid grid-cols-2 sm:grid-cols-4 gap-3">
        <div className="bg-gray-50 rounded-xl border border-gray-100 p-4">
          <div className="w-8 h-8 flex items-center justify-center bg-[#1a1a1a] rounded-lg mb-3">
            <i className="ri-history-line text-white text-sm"></i>
          </div>
          <p className="text-xl font-black text-[#1a1a1a]">{stats.total.toLocaleString()}</p>
          <p className="text-xs text-gray-400 mt-0.5">إجمالي العمليات</p>
        </div>
        <div className="bg-gray-50 rounded-xl border border-gray-100 p-4">
          <div className="w-8 h-8 flex items-center justify-center bg-amber-400 rounded-lg mb-3">
            <i className="ri-calendar-check-line text-black text-sm"></i>
          </div>
          <p className="text-xl font-black text-[#1a1a1a]">{stats.today}</p>
          <p className="text-xs text-gray-400 mt-0.5">عمليات اليوم</p>
        </div>
        <div className="bg-gray-50 rounded-xl border border-gray-100 p-4">
          <div className="w-8 h-8 flex items-center justify-center bg-emerald-100 rounded-lg mb-3">
            <i className="ri-shopping-bag-3-line text-emerald-600 text-sm"></i>
          </div>
          <p className="text-xl font-black text-[#1a1a1a]">{stats.byEntity['product'] ?? 0}</p>
          <p className="text-xs text-gray-400 mt-0.5">عمليات المنتجات</p>
        </div>
        <div className="bg-gray-50 rounded-xl border border-gray-100 p-4">
          <div className="w-8 h-8 flex items-center justify-center bg-sky-100 rounded-lg mb-3">
            <i className="ri-file-list-3-line text-sky-600 text-sm"></i>
          </div>
          <p className="text-xl font-black text-[#1a1a1a]">{stats.byEntity['order'] ?? 0}</p>
          <p className="text-xs text-gray-400 mt-0.5">عمليات الطلبات</p>
        </div>
      </div>

      {/* Entity Filter Tabs */}
      <div className="flex items-center gap-1 overflow-x-auto pb-1">
        {ENTITY_FILTER_OPTIONS.map(opt => (
          <button
            key={opt.value}
            onClick={() => setEntityFilter(opt.value)}
            className={`flex items-center gap-1.5 px-3 py-2 rounded-xl text-xs font-bold whitespace-nowrap transition-all cursor-pointer flex-shrink-0 ${
              entityFilter === opt.value
                ? 'bg-[#1a1a1a] text-white'
                : 'bg-gray-50 text-gray-500 hover:bg-gray-100 border border-gray-100'
            }`}
          >
            <i className={`${opt.icon} text-sm`}></i>
            {opt.label}
            {opt.value !== 'all' && stats.byEntity[opt.value] > 0 && (
              <span className={`text-[10px] font-black px-1.5 py-0.5 rounded-full ${
                entityFilter === opt.value ? 'bg-white/20 text-white' : 'bg-gray-200 text-gray-600'
              }`}>
                {stats.byEntity[opt.value]}
              </span>
            )}
          </button>
        ))}
      </div>

      {/* Search + Date Filter */}
      <div className="flex flex-col sm:flex-row gap-2">
        <div className="relative flex-1">
          <i className="ri-search-line absolute top-1/2 -translate-y-1/2 right-3 text-gray-300 text-sm"></i>
          <input
            type="text"
            value={search}
            onChange={e => setSearch(e.target.value)}
            placeholder="بحث بالاسم، البريد، رقم الطلب..."
            className="w-full border border-gray-200 rounded-xl pr-9 pl-4 py-2.5 text-sm outline-none focus:border-[#1a1a1a] transition-colors"
          />
          {search && (
            <button onClick={() => setSearch('')} className="absolute top-1/2 -translate-y-1/2 left-3 text-gray-400 hover:text-gray-600 cursor-pointer">
              <i className="ri-close-line text-sm"></i>
            </button>
          )}
        </div>
        <input
          type="date"
          value={dateFrom}
          onChange={e => setDateFrom(e.target.value)}
          className="border border-gray-200 rounded-xl px-3 py-2.5 text-sm outline-none focus:border-[#1a1a1a] transition-colors cursor-pointer"
          title="من تاريخ"
        />
        <input
          type="date"
          value={dateTo}
          onChange={e => setDateTo(e.target.value)}
          min={dateFrom}
          className="border border-gray-200 rounded-xl px-3 py-2.5 text-sm outline-none focus:border-[#1a1a1a] transition-colors cursor-pointer"
          title="إلى تاريخ"
        />
        {(dateFrom || dateTo) && (
          <button
            onClick={() => { setDateFrom(''); setDateTo(''); }}
            className="flex items-center gap-1.5 px-3 py-2.5 rounded-xl border border-red-200 bg-red-50 text-red-600 text-xs font-bold cursor-pointer whitespace-nowrap hover:bg-red-100 transition-colors"
          >
            <i className="ri-close-line text-sm"></i>
            مسح
          </button>
        )}
      </div>

      {/* Log List */}
      {loading ? (
        <div className="flex flex-col items-center justify-center py-16 gap-3">
          <div className="w-8 h-8 border-2 border-gray-200 border-t-[#1a1a1a] rounded-full animate-spin"></div>
          <p className="text-sm text-gray-400">جاري تحميل السجل...</p>
        </div>
      ) : filtered.length === 0 ? (
        <div className="flex flex-col items-center justify-center py-16 gap-3">
          <div className="w-14 h-14 flex items-center justify-center bg-gray-50 rounded-2xl">
            <i className="ri-history-line text-gray-300 text-2xl"></i>
          </div>
          <p className="text-sm text-gray-400">لا توجد عمليات مسجّلة بعد</p>
          <p className="text-xs text-gray-300">ستظهر العمليات هنا تلقائياً عند إجراء أي تغيير</p>
        </div>
      ) : (
        <div className="flex flex-col gap-6">
          {grouped.map(group => (
            <div key={group.date}>
              {/* Date separator */}
              <div className="flex items-center gap-3 mb-3">
                <div className="h-px flex-1 bg-gray-100"></div>
                <span className="text-[11px] font-bold text-gray-400 whitespace-nowrap px-2">{group.date}</span>
                <div className="h-px flex-1 bg-gray-100"></div>
              </div>

              {/* Entries */}
              <div className="flex flex-col gap-2">
                {group.items.map(entry => {
                  const meta = ACTION_META[entry.action_type as ActionType] ?? {
                    label: entry.action_type, icon: 'ri-history-line',
                    color: 'text-gray-600', bg: 'bg-gray-50', border: 'border-gray-200',
                  };
                  const detailText = buildDetailText(entry);
                  const avatarColor = getAvatarColor(entry.admin_email);

                  return (
                    <div
                      key={entry.id}
                      className="flex items-start gap-3 bg-white rounded-xl border border-gray-100 px-4 py-3 hover:border-gray-200 transition-colors"
                    >
                      {/* Action icon */}
                      <div className={`w-8 h-8 flex items-center justify-center rounded-lg flex-shrink-0 mt-0.5 border ${meta.bg} ${meta.border}`}>
                        <i className={`${meta.icon} text-sm ${meta.color}`}></i>
                      </div>

                      {/* Content */}
                      <div className="flex-1 min-w-0">
                        <div className="flex items-start justify-between gap-2 flex-wrap">
                          <div className="flex-1 min-w-0">
                            <div className="flex items-center gap-2 flex-wrap">
                              <span className={`text-xs font-bold ${meta.color}`}>{meta.label}</span>
                              {entry.entity_name && (
                                <>
                                  <span className="text-gray-300 text-xs">·</span>
                                  <span className="text-xs font-medium text-[#1a1a1a] truncate max-w-[200px]">
                                    {entry.entity_name}
                                  </span>
                                </>
                              )}
                              {entry.entity_id && entry.entity_type === 'order' && (
                                <span className="text-[10px] font-bold text-gray-400 bg-gray-50 border border-gray-200 px-1.5 py-0.5 rounded-full">
                                  #{entry.entity_id}
                                </span>
                              )}
                            </div>
                            {detailText && (
                              <p className="text-[11px] text-gray-400 mt-0.5">{detailText}</p>
                            )}
                          </div>
                          <span className="text-[10px] text-gray-400 whitespace-nowrap flex-shrink-0" title={formatFullDate(entry.created_at)}>
                            {formatRelativeTime(entry.created_at)}
                          </span>
                        </div>

                        {/* Admin info */}
                        <div className="flex items-center gap-1.5 mt-1.5">
                          <div className={`w-5 h-5 flex items-center justify-center rounded-full text-[9px] font-black flex-shrink-0 ${avatarColor}`}>
                            {getInitials(entry.admin_email, entry.admin_name)}
                          </div>
                          <span className="text-[11px] text-gray-400">
                            {entry.admin_name || entry.admin_email}
                          </span>
                          <span className="text-gray-200 text-xs">·</span>
                          <span className="text-[10px] text-gray-300 truncate" dir="ltr">{entry.admin_email}</span>
                        </div>
                      </div>
                    </div>
                  );
                })}
              </div>
            </div>
          ))}

          {/* Load More */}
          {hasMore && !search && (
            <button
              onClick={() => fetchEntries(false)}
              disabled={loadingMore}
              className="flex items-center justify-center gap-2 w-full py-3 border border-gray-200 rounded-xl text-sm font-bold text-gray-500 hover:bg-gray-50 hover:text-gray-700 transition-colors cursor-pointer disabled:opacity-50"
            >
              {loadingMore ? (
                <><i className="ri-loader-4-line animate-spin"></i> جاري التحميل...</>
              ) : (
                <><i className="ri-arrow-down-line"></i> تحميل المزيد</>
              )}
            </button>
          )}
        </div>
      )}
    </div>
  );
}
